import * as React from 'react';

import { Checkbox, Col, Form, Input, Row } from 'antd';
import { useAppDispatch, useAppSelector } from 'src/types/reduxHelpers';

import { InterviewComponent } from 'src/components/interviewComponents/types/interviewComponent';
import { css } from '@emotion/react';
import { nameof } from 'ts-simple-nameof';
import { setReasonsForLeaving } from 'src/store/modules/interviewModule';
import { useReasonForLeavingOptions } from 'src/components/interviewComponents/hooks';
import { useTranslation } from 'react-i18next';

interface ReasonForLeavingProps {

}

interface ReasonForLeavingFormValues {
    reasons: Array<string>;
    customReason?: string;
}

export const ReasonForLeaving: InterviewComponent<ReasonForLeavingProps> = React.forwardRef((props, ref) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const staticReasonOptions = useReasonForLeavingOptions();

    const [form] = Form.useForm<ReasonForLeavingFormValues>();

    const reasonsForLeaving = useAppSelector((state) => state.interview.reasonsForLeaving);
    const otherOptionValue = reasonsForLeaving?.reasons.filter(x => !staticReasonOptions.map((reason) => reason.value).includes(x))?.[0];

    const [isOtherSelected, setIsOtherSelected] = React.useState(!!otherOptionValue);

    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                await form.validateFields();

                form.submit();

                return true;
            } catch (err) {
                return false;
            }
        },
    }));

    return (
        <Row
            align="middle"
            justify="center"
            gutter={[30, 20]}
        >
            <Col
                span={24}
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={(values) => {
                        const allReasons: Array<string> = [];
                        if (values.reasons?.length > 0) {
                            // Add our static reasons, if any exist
                            allReasons.push(...values.reasons.filter(x => staticReasonOptions.map((reason) => reason.value).includes(x)));
                        }

                        if (isOtherSelected && !!values.customReason) {
                            // Add our custom reason, if it exists and is selected
                            allReasons.push(values.customReason);
                        }

                        dispatch(setReasonsForLeaving({
                            reasons: allReasons,
                        }));
                    }}
                >
                    <Form.Item
                        // We're going to handle the validation for this item via the 'customReason' option below so that it behaves as if
                        // all of the options (including the custom one) belong to the same list, so we wont add any rules here
                        label={t('Components.InterviewComponents.ReasonForLeaving.reason_for_leaving_label')}
                        name={nameof<ReasonForLeavingFormValues>(x => x.reasons)}
                        required={true}
                        initialValue={reasonsForLeaving?.reasons}
                        style={{
                            marginBottom: 15,
                        }}
                        css={css({
                            // Antd seems to recommend using a <Space> component with children to make checkbox items layout vertically instead of horizontally, but
                            // this doesn't work when using the 'options' prop rather than having <Checkbox> children

                            // Change the styles for our Checkbox.Group to display options vertically
                            // Ref https://stackoverflow.com/a/68440242/3253311
                            '.ant-checkbox-group': {
                                display: 'flex',
                                flexDirection: 'column',
                                '.ant-checkbox-group-item': {
                                    marginInlineStart: 0,
                                },
                            },
                        })}
                    >
                        <Checkbox.Group
                            onChange={async () => await form.validateFields([nameof<ReasonForLeavingFormValues>(x => x.customReason)])}
                            options={staticReasonOptions}
                        />
                    </Form.Item>

                    <Checkbox
                        checked={isOtherSelected}
                        onChange={async (e) => {
                            setIsOtherSelected(e.target.checked);

                            // Run the validation when the 'other' checkbox is changed
                            await form.validateFields([nameof<ReasonForLeavingFormValues>(x => x.customReason)]);
                        }}
                    >
                        {t('Common.other')}
                    </Checkbox>

                    <Form.Item
                        name={nameof<ReasonForLeavingFormValues>(x => x.customReason)}
                        rules={[
                            {
                                // Here's where we'll handle the validation for both the static list of reasons, and the custom reason option
                                validator: (_rule, value) => {
                                    // Get all the reasons that are selected from the static list...
                                    // This could include the 'other' option if the data has already been set and been passed in to edit
                                    const allReasons = [...(form.getFieldValue(nameof<ReasonForLeavingFormValues>(x => x.reasons)) ?? [])] as Array<string>;

                                    // Get a list of only the static reasons that have been selected
                                    const finalReasons = allReasons.filter(reason => staticReasonOptions.map((r) => r.value).includes(reason));

                                    if (!!value && isOtherSelected) {
                                        // If the 'other' option is selected and has a value, add it as well
                                        finalReasons.push(value);
                                    }

                                    if (finalReasons.length < 1) {
                                        // Error if we don't have any reasons selected between the static options and the custom one
                                        return Promise.reject(t('Common.validation.required_field_at_least_one_message'));
                                    }

                                    if (isOtherSelected && !value) {
                                        // If 'other' is selected, but no value was written, give an error
                                        return Promise.reject(t('Components.InterviewComponents.ReasonForLeaving.validation.other_reason_missing_value_message'));
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ]}
                        initialValue={otherOptionValue}
                    >
                        <Input disabled={!isOtherSelected} />
                    </Form.Item>
                </Form>
            </Col>
        </Row>
    );
});

ReasonForLeaving.displayName = 'ReasonForLeaving';